import { Skeleton } from "@bsf/force-ui";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { useState } from "react";
import { __ } from '@wordpress/i18n';

const videoData = [
    {
        id: 1,
        videoId: "pJtN81Qdwbk",
        title: __("Video Tutorial 1", 'ultimate_vc'),
        image: `${uavcSettingsData.highlight_url}`
    },
    {
        id: 2,
        videoId: "5S7CmFGpaUU",
        title: __("Video Tutorial 2", 'ultimate_vc'),
        image: `${uavcSettingsData.info_banner_url}`
    },
    {
        id: 3,
        videoId: "6N9MsInvkoI",
        title: __("Video Tutorial 3", 'ultimate_vc'),
        image: `${uavcSettingsData.fancy_text_url}`
    },
    {
        id: 4,
        videoId: "Pjz2CdmS_VQ",
        title: __("Video Tutorial 4", 'ultimate_vc'),
        image: `${uavcSettingsData.ihover_url}`
    },
    {
        id: 5,
        videoId: "fmCZS89LyYk",
        title: __("Video Tutorial 5", 'ultimate_vc'),
        // image: `${uavcSettingsData.highlight_url}`
    },
    {
        id: 6,
        videoId: "SJCwSVB7wmM",
        title: __("Video Tutorial 6", 'ultimate_vc'),
        image: `${uavcSettingsData.row_separator_effect_url}`
    },
    {
        id: 7,
        videoId: "yDf4Lsm_BLY",
        title: __("Video Tutorial 7", 'ultimate_vc'),
        image: `${uavcSettingsData.parallax_fade_effect_url}`
    },
    {
        id: 8,
        videoId: "yXYDgEa_fhQ",
        title: __("Video Tutorial 8", 'ultimate_vc'),
        image: `${uavcSettingsData.overlay_effects_url}`
    },
    {
        id: 9,
        videoId: "QdZs5aQlXe8",
        title: __("Video Tutorial 9", 'ultimate_vc'),
        image: `${uavcSettingsData.advanced_carousel_url}`
    },
    {
        id: 10,
        videoId: "5yjSle_dmxM",
        title: __("Video Tutorial 10", 'ultimate_vc'),
        image: `${uavcSettingsData.interactive_banner_2_url}`
    },
    {
        id: 11,
        videoId: "VQ3AP6y6GJs",
        title: __("Video Tutorial 11", 'ultimate_vc'),
        image: `${uavcSettingsData.countdown_url}`
    },
    {
        id: 12,
        videoId: "zPs14m5ngXY",
        title: __("Video Tutorial 12", 'ultimate_vc'),
        image: `${uavcSettingsData.counter_url}`
    },
    {
        id: 13,
        videoId: "FfOHTX-xB8E",
        title: __("Video Tutorial 13", 'ultimate_vc'),
        image: `${uavcSettingsData.google_trends_url}`
    },
    {
        id: 14,
        videoId: "D8QIox7kTuY",
        title: __("Video Tutorial 14", 'ultimate_vc'),
        image: `${uavcSettingsData.flip_box_url}`
    }
];

// New component to handle lazy-loading of YouTube thumbnails
const VideoThumbnail = ({ videoId, title, image, playingVideoId, setPlayingVideoId }) => {
    const isPlaying = playingVideoId === videoId;

    const handlePlayVideo = () => {
        setPlayingVideoId(videoId);
    };

    return (
        <div className="video-thumbnail-container" onClick={handlePlayVideo} style={{ cursor: 'pointer' }}>
            {isPlaying ? (
                <iframe
                    width="235"
                    height="145"
                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&list=PL1kzJGWGPrW9CDWwdAWrd_9YQsh1z7u6O`}
                    title="YouTube Playlist"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{  maxWidth: "720px"}}
                />
            ) : (
                <div style={{ position: 'relative' }}>
                    <img
                        src={image ? image : `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                        alt={title}
                        width="235"
                        height="145"
                        style={{ borderRadius: "8px" }}
                    />
                    <img
                        src={`${uavcSettingsData.video_control}`}
                        alt="Play"
                        style={{ left: "43%", top: "50%", transform: "translate(-50%, -50%)" }}
                        className="absolute inset-0 w-11 h-11"
                    />
                </div>
            )}
        </div>
    );
};

// VideoTutorials Component
export default function VideoTutorials() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [playingVideoId, setPlayingVideoId] = useState(null); // Track the currently playing video

    const nextSlide = () => {
        if (currentIndex < videoData.length - 3) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const prevSlide = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    return (
        <div className="rounded-lg bg-white w-full mb-4">
            <div
                className="flex items-center justify-between p-4"
                style={{
                    paddingBottom: "0",
                }}
            >
                <p className="m-0 text-sm pt-4 font-semibold text-text-primary">
                    {__("Video Tutorials", "ultimate_vc")}
                </p>
                <div className="flex items-center cursor-pointer gap-x-2">
                    <ChevronLeft
                        className={
                            currentIndex === 0 ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"
                        }
                        onClick={prevSlide}
                    />
                    <ChevronRight
                        className={
                            currentIndex >= videoData.length - 3
                                ? "text-gray-300 cursor-not-allowed"
                                : "cursor-pointer"
                        }
                        onClick={nextSlide}
                    />
                </div>
            </div>
            <div className="overflow-hidden bg-black flex-col rounded-lg px-4 py-2 relative">
                <div
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{
                        transform: `translateX(-${currentIndex * (100 / videoData.length)}%)`,
                        width: `${(videoData.length / 2.95) * 100}%`,
                        gap: "0.7rem",
                    }}
                >
                    {videoData.map((video) => (
                        <div  key={video.id} className="w-1/3 py-2 rounded-md px-1">
                            <VideoThumbnail
                                videoId={video.videoId}
                                title={video.title}
                                image={video.image}
                                playingVideoId={playingVideoId}
                                setPlayingVideoId={setPlayingVideoId}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

